import { useEffect, useRef } from 'react';
import useAuthStore from '../stores/authStore';
import { refreshToken } from '../services/authService';

// 30 分鐘續期一次，遠小於後端 JWT 8 小時有效期，確保頁面開著時 token 永不過期
const REFRESH_INTERVAL_MS = 30 * 60 * 1000;

/**
 * 主動續期 token：登入期間定時呼叫 /auth/refresh，讓後端重新簽發 JWT（httpOnly cookie），
 * 使 token 不會因 8 小時有效期到期而把使用者登出（符合「閒置逾時 = 0 不自動登出」的預期）。
 * 閒置登出仍由 useIdleTimer 依設定獨立處理，兩者互不影響。
 * 由 App.tsx 呼叫一次即可。
 */
export default function useTokenRefresh() {
  const token = useAuthStore((s) => s.token);
  const timerRef = useRef<ReturnType<typeof setInterval> | null>(null);

  useEffect(() => {
    if (!token) return;
    const tick = async () => {
      try {
        await refreshToken();
      } catch {
        // 續期失敗（token 已真的失效或網路問題）→ 後續 API 的 401 攔截器會自然導回登入
      }
    };
    tick();  // 進頁/重載即先續期一次，避免「開很久的分頁重載時剛好接近到期」被登出
    timerRef.current = setInterval(tick, REFRESH_INTERVAL_MS);
    return () => { if (timerRef.current) clearInterval(timerRef.current); };
  }, [token]);
}
